import { createContext, useContext, useState, useEffect, useMemo } from 'react';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
];

const TRANSLATIONS = {
  en: {
    // Nav
    nav_dashboard: 'Dashboard',
    nav_new_order: 'New Order',
    nav_services: 'Services',
    nav_orders: 'Orders',
    nav_deposit: 'Deposit',
    nav_virtual_numbers: 'Virtual Numbers',
    nav_referrals: 'Referral Program',
    nav_settings: 'Settings',
    nav_support: 'Support',
    nav_guide: 'Guide',
    nav_logout: 'Logout',
    nav_login: 'Log In',
    nav_signup: 'Get Started',
    nav_features: 'Features',
    nav_platforms: 'Platforms',
    nav_how_it_works: 'How It Works',
    nav_pricing: 'Pricing',
    nav_faq: 'FAQ',
    nav_language: 'Language',

    // Settings
    settings_title: 'Account Settings',
    settings_subtitle: 'Manage your profile preferences, regional currency, security credentials, and API access.',
    tab_preferences: 'Preferences & Currency',
    tab_security: 'Security & Password',
    tab_api: 'API & Reseller Key',
    loc_title: 'Localization & Currency',
    loc_subtitle: 'Set your location, preferred language and currency for orders, balance, and payment methods.',
    field_country: 'Primary Country',
    field_country_desc: 'Mobile Money and localized payment options will default to this country.',
    field_currency: 'Account Display Currency',
    field_language: 'Preferred Language',
    field_language_desc: 'Select the display language for the platform interface and notifications.',
    btn_save_preferences: 'Save Preferences',
    btn_saving_preferences: 'Updating Preferences...',
    msg_preferences_saved: 'Preferences updated successfully!',

    // Common
    available_balance: 'Available Balance',
    add_funds: 'Add Funds',
    place_order: 'Place Order',
    category: 'Category',
    service: 'Service',
    quantity: 'Quantity',
    link: 'Link',
    total_cost: 'Total Cost',
    status: 'Status',
    actions: 'Actions',
    search: 'Search',
    cancel: 'Cancel',
    loading: 'Loading...',
    close: 'Close',
  },
  fr: {
    // Nav
    nav_dashboard: 'Tableau de bord',
    nav_new_order: 'Nouvelle commande',
    nav_services: 'Services',
    nav_orders: 'Commandes',
    nav_deposit: 'Recharger',
    nav_virtual_numbers: 'Numéros virtuels',
    nav_referrals: 'Parrainage',
    nav_settings: 'Paramètres',
    nav_support: 'Support',
    nav_guide: 'Guide',
    nav_logout: 'Déconnexion',
    nav_login: 'Connexion',
    nav_signup: 'Commencer',
    nav_features: 'Fonctionnalités',
    nav_platforms: 'Plateformes',
    nav_how_it_works: 'Comment ça marche',
    nav_pricing: 'Tarifs',
    nav_faq: 'FAQ',
    nav_language: 'Langue',

    // Settings
    settings_title: 'Paramètres du compte',
    settings_subtitle: 'Gérez vos préférences de profil, devise régionale, sécurité et accès API.',
    tab_preferences: 'Préférences & Devise',
    tab_security: 'Sécurité & Mot de passe',
    tab_api: 'Clé API & Revendeur',
    loc_title: 'Localisation, Devise & Langue',
    loc_subtitle: 'Définissez votre pays, langue préférée et devise pour vos commandes et paiements.',
    field_country: 'Pays principal',
    field_country_desc: 'Les paiements Mobile Money et options locales utiliseront ce pays par défaut.',
    field_currency: 'Devise d\'affichage du compte',
    field_language: 'Langue préférée',
    field_language_desc: 'Sélectionnez la langue d\'affichage de la plateforme et des notifications.',
    btn_save_preferences: 'Enregistrer les préférences',
    btn_saving_preferences: 'Mise à jour en cours...',
    msg_preferences_saved: 'Préférences mises à jour avec succès !',

    // Common
    available_balance: 'Solde disponible',
    add_funds: 'Recharger',
    place_order: 'Commander',
    category: 'Catégorie',
    service: 'Service',
    quantity: 'Quantité',
    link: 'Lien',
    total_cost: 'Coût total',
    status: 'Statut',
    actions: 'Actions',
    search: 'Rechercher',
    cancel: 'Annuler',
    loading: 'Chargement...',
    close: 'Fermer',
  },
  es: {
    // Nav
    nav_dashboard: 'Panel de control',
    nav_new_order: 'Nuevo pedido',
    nav_services: 'Servicios',
    nav_orders: 'Pedidos',
    nav_deposit: 'Recargar',
    nav_virtual_numbers: 'Números virtuales',
    nav_referrals: 'Programa de referidos',
    nav_settings: 'Ajustes',
    nav_support: 'Soporte',
    nav_guide: 'Guía',
    nav_logout: 'Cerrar sesión',
    nav_login: 'Iniciar sesión',
    nav_signup: 'Empezar gratis',
    nav_features: 'Características',
    nav_platforms: 'Plataformas',
    nav_how_it_works: 'Cómo funciona',
    nav_pricing: 'Precios',
    nav_faq: 'Preguntas frecuentes',
    nav_language: 'Idioma',

    // Settings
    settings_title: 'Ajustes de la cuenta',
    settings_subtitle: 'Administre sus preferencias de perfil, moneda regional, seguridad y acceso a la API.',
    tab_preferences: 'Preferencias y moneda',
    tab_security: 'Seguridad y contraseña',
    tab_api: 'Clave API y revendedor',
    loc_title: 'Localización, moneda e idioma',
    loc_subtitle: 'Configure su país, idioma y moneda preferidos para sus pedidos y saldo.',
    field_country: 'País principal',
    field_country_desc: 'Las opciones de pago locales y Mobile Money se adaptarán a este país.',
    field_currency: 'Moneda de la cuenta',
    field_language: 'Idioma preferido',
    field_language_desc: 'Seleccione el idioma de visualización de la plataforma y notificaciones.',
    btn_save_preferences: 'Guardar preferencias',
    btn_saving_preferences: 'Guardando...',
    msg_preferences_saved: '¡Preferencias actualizadas con éxito!',

    // Common
    available_balance: 'Saldo disponible',
    add_funds: 'Añadir fondos',
    place_order: 'Realizar pedido',
    category: 'Categoría',
    service: 'Servicio',
    quantity: 'Cantidad',
    link: 'Enlace',
    total_cost: 'Coste total',
    status: 'Estado',
    actions: 'Acciones',
    search: 'Buscar',
    cancel: 'Cancelar',
    loading: 'Cargando...',
    close: 'Cerrar',
  },
  ar: {
    // Nav
    nav_dashboard: 'لوحة التحكم',
    nav_new_order: 'طلب جديد',
    nav_services: 'الخدمات',
    nav_orders: 'الطلبات',
    nav_deposit: 'شحن الرصيد',
    nav_virtual_numbers: 'أرقام وهمية',
    nav_referrals: 'نظام الإحالة',
    nav_settings: 'الإعدادات',
    nav_support: 'الدعم الفني',
    nav_guide: 'دليل الاستخدام',
    nav_logout: 'تسجيل الخروج',
    nav_login: 'تسجيل الدخول',
    nav_signup: 'ابدأ الآن',
    nav_features: 'المميزات',
    nav_platforms: 'المنصات',
    nav_how_it_works: 'كيف يعمل',
    nav_pricing: 'الأسعار',
    nav_faq: 'الأسئلة الشائعة',
    nav_language: 'اللغة',

    // Settings
    settings_title: 'إعدادات الحساب',
    settings_subtitle: 'إدارة تفضيلات الحساب، العملة، الأمان ومفاتيح API.',
    tab_preferences: 'التفضيلات والعملة',
    tab_security: 'الأمان وكلمة المرور',
    tab_api: 'مفتاح API والموزع',
    loc_title: 'الموقع والعملة واللغة',
    loc_subtitle: 'حدد بلدك ولغتك المفضلة وعملتك للطلبات وطرق الدفع.',
    field_country: 'الدولة الأساسية',
    field_country_desc: 'خيارات الدفع وMobile Money ستكون مخصصة لهذه الدولة.',
    field_currency: 'عملة الحساب',
    field_language: 'اللغة المفضلة',
    field_language_desc: 'اختر لغة واجهة المنصة والإشعارات.',
    btn_save_preferences: 'حفظ التفضيلات',
    btn_saving_preferences: 'جاري التحديث...',
    msg_preferences_saved: 'تم تحديث التفضيلات بنجاح!',

    // Common
    available_balance: 'الرصيد المتاح',
    add_funds: 'إضافة رصيد',
    place_order: 'تنفيذ الطلب',
    category: 'القسم',
    service: 'الخدمة',
    quantity: 'الكمية',
    link: 'الرابط',
    total_cost: 'التكلفة الإجمالية',
    status: 'الحالة',
    actions: 'الإجراءات',
    search: 'بحث',
    cancel: 'إلغاء',
    loading: 'جاري التحميل...',
    close: 'إغلاق',
  },
  pt: {
    // Nav
    nav_dashboard: 'Painel',
    nav_new_order: 'Novo Pedido',
    nav_services: 'Serviços',
    nav_orders: 'Pedidos',
    nav_deposit: 'Depositar',
    nav_virtual_numbers: 'Números Virtuais',
    nav_referrals: 'Programa de Afiliados',
    nav_settings: 'Configurações',
    nav_support: 'Suporte',
    nav_guide: 'Guia',
    nav_logout: 'Sair',
    nav_login: 'Entrar',
    nav_signup: 'Começar Grátis',
    nav_features: 'Recursos',
    nav_platforms: 'Plataformas',
    nav_how_it_works: 'Como Funciona',
    nav_pricing: 'Preços',
    nav_faq: 'Perguntas Frequentes',
    nav_language: 'Idioma',

    // Settings
    settings_title: 'Configurações da Conta',
    settings_subtitle: 'Gerencie suas preferências, moeda regional, credenciais e API.',
    tab_preferences: 'Preferências e Moeda',
    tab_security: 'Segurança e Senha',
    tab_api: 'Chave API e Revenda',
    loc_title: 'Localização, Moeda e Idioma',
    loc_subtitle: 'Defina seu país, idioma preferido e moeda para pedidos e saldo.',
    field_country: 'País Principal',
    field_country_desc: 'Opções de pagamento locais e Mobile Money usarão este país por padrão.',
    field_currency: 'Moeda da Conta',
    field_language: 'Idioma Preferido',
    field_language_desc: 'Selecione o idioma de exibição da plataforma e notificações.',
    btn_save_preferences: 'Salvar Preferências',
    btn_saving_preferences: 'Atualizando...',
    msg_preferences_saved: 'Preferências atualizadas com sucesso!',

    // Common
    available_balance: 'Saldo Disponível',
    add_funds: 'Adicionar Saldo',
    place_order: 'Fazer Pedido',
    category: 'Categoria',
    service: 'Serviço',
    quantity: 'Quantidade',
    link: 'Link',
    total_cost: 'Custo Total',
    status: 'Status',
    actions: 'Ações',
    search: 'Pesquisar',
    cancel: 'Cancelar',
    loading: 'Carregando...',
    close: 'Fechar',
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('nb_language');
    if (saved && TRANSLATIONS[saved]) return saved;
    // Auto-detect browser language if french
    const browserLang = (navigator.language || navigator.userLanguage || '').substring(0, 2).toLowerCase();
    if (TRANSLATIONS[browserLang]) return browserLang;
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('nb_language', language);
    document.documentElement.setAttribute('lang', language);
    const langObj = SUPPORTED_LANGUAGES.find(l => l.code === language);
    if (langObj?.dir === 'rtl') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [language]);

  const setLanguage = (newLang) => {
    if (TRANSLATIONS[newLang]) {
      setLanguageState(newLang);
    }
  };

  const t = useMemo(() => {
    return (key, fallback) => {
      const currentDict = TRANSLATIONS[language] || TRANSLATIONS.en;
      if (currentDict && currentDict[key]) return currentDict[key];
      if (TRANSLATIONS.en[key]) return TRANSLATIONS.en[key];
      return fallback !== undefined ? fallback : key;
    };
  }, [language]);

  const currentLanguageObj = useMemo(() => {
    return SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];
  }, [language]);

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      t,
      languages: SUPPORTED_LANGUAGES,
      currentLanguage: currentLanguageObj
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
